import type React from 'react';

import type { CellContext, HeaderContext } from '@tanstack/react-table';
import type { EventRecord } from '@vlabs/api/artemy/model';
import type { Event } from '@vlabs/api/luna-api/model';

import type { TBuildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';
import { buildTableViewCell } from '@vlabs/monokit/shared/ui/table-view';

type EventData = EventRecord | Event;

type TSharedCtx<TValue = unknown> = CellContext<EventData, TValue> | HeaderContext<EventData, TValue>;

const dataTestIdFromCtx = <TValue extends React.ReactNode>(
  ctx: TSharedCtx<TValue>,
): {
  'data-testid': string;
} | undefined => {
  if (!ctx) return undefined;
  if ((ctx as CellContext<EventData, TValue>).row) {
    const { row } = (ctx as CellContext<EventData, TValue>);
    return {
      'data-testid': `${ctx.column.id}.cell.${row.id}`,
    };
  }
  return {
    'data-testid': `${ctx.column.id}.header`,
  };
};

export function renderHeader<TValue extends React.ReactNode>(
  renderer: (ctx: TSharedCtx<TValue>) => TBuildTableViewCell,
  value: React.ReactNode = '',
) {
  return (header: HeaderContext<EventData, TValue>): React.JSX.Element => renderer(header)(value);
}

export function renderCell<TValue extends React.ReactNode>(
  renderer: (ctx: TSharedCtx<TValue>) => TBuildTableViewCell,
  wrap?: (ctx: CellContext<EventData, TValue>) => React.ReactNode,
) {
  return (ctx: CellContext<EventData, TValue>): React.JSX.Element => renderer(ctx)(
    wrap ? wrap(ctx) : ctx.getValue(),
  );
}

export const sampleCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 208px',
    padding: '16px 12px 16px 16px',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const topMatchCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 372px',
    padding: '16px 12px 16px 0px',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const eventInfoCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '0 0 372px',
    padding: '16px 12px 16px 0px',
    alignSelf: 'stretch',
    display: 'flex',
  },
  ...dataTestIdFromCtx<string>(ctx),
});

export const attributesCell = (ctx: TSharedCtx<string>): TBuildTableViewCell => buildTableViewCell({
  style: {
    flex: '1 1 0',
    padding: '16px 16px 16px 0px',
    alignSelf: 'stretch',
    display: 'flex',
  },
  ...dataTestIdFromCtx<string>(ctx),
});
